import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import Checkbox from 'views/ui/form_elements/checkbox';
import { Theme } from 'ui_system/theme';

import { PrivacyPolicyContainer } from './styles';

const PolicyLink = styled.a`
  color: ${Theme.colors.linksDesignElementsBaseColor};
  text-decoration: underline;
`;

const ANYROAD_TERMS_URL = 'https://www.anyroad.com/end-user-terms';
const ANYROAD_PRIVACY_URL = 'https://www.anyroad.com/privacy';

interface FormFieldProps {
  name: string;
  value: any;
  errors?: string[];
  onChange: (field: string, value: any) => void;
}

interface PrivacyPolicyProps {
  fields: {
    get: (fieldName: string) => any;
  };
  errors: {
    get: (fieldName: string) => string[] | undefined;
  };
  onChange: (field: string, value: any) => void;
  privacy_policy_link?: string;
  privacy_policy_link_text?: string;
  anyroadPrivacyPolicy?: boolean;
}

export const PrivacyPolicy = ({
  fields,
  errors,
  onChange,
  privacy_policy_link: privacyPolicyLink = '',
  privacy_policy_link_text: privacyPolicyLinkText = '',
  anyroadPrivacyPolicy = false,
}: PrivacyPolicyProps) => {
  const { formatMessage } = useIntl();
  const getFormProps = useCallback(
    (fieldName: string): FormFieldProps => {
      return {
        name: fieldName,
        value: fields.get(fieldName),
        errors: errors.get(fieldName),
        onChange,
      };
    },
    [fields, errors, onChange]
  );

  useEffect(() => {
    const guidePrivacyPolicyProps = getFormProps('privacy_policy_accepted');
    const anyroadPrivacyPolicyProps = getFormProps('anyroad_privacy_policy');

    if (guidePrivacyPolicyProps.errors && guidePrivacyPolicyProps.errors[0]) {
      guidePrivacyPolicyProps.errors[0] = formatMessage({
        defaultMessage: 'Please accept our Privacy Policy',
        description: 'Privacy policy acceptance error message',
      });
    }
    if (
      anyroadPrivacyPolicyProps.errors &&
      anyroadPrivacyPolicyProps.errors[0]
    ) {
      anyroadPrivacyPolicyProps.errors[0] =
        // eslint-disable-next-line quotes
        "Please accept AnyRoad's Terms of Service and Privacy Policy.";
    }
  }, [errors, getFormProps, formatMessage]);

  const renderGuidePrivacyPolicy = () => {
    const privacyPoliciesAndLinks = [
      privacyPolicyLink.split('\n'),
      privacyPolicyLinkText.split('\n'),
    ];
    const firstLink = {
      url: privacyPoliciesAndLinks[0][0],
      text: privacyPoliciesAndLinks[1][0],
    };
    const secondLink =
      privacyPoliciesAndLinks[0].length > 1
        ? {
            url: privacyPoliciesAndLinks[0][1],
            text: privacyPoliciesAndLinks[1][1],
          }
        : null;

    return (
      <Checkbox
        {...getFormProps('privacy_policy_accepted')}
        dataTestProp="privacy-policy-checkbox"
      >
        {secondLink ? (
          <FormattedMessage
            defaultMessage="Please confirm that you have read and accept our <firstLink>{firstLinkText}</firstLink> and <secondLink>{secondLinkText}</secondLink>"
            description="Privacy policy confirmation message with two links"
            values={{
              firstLinkText: firstLink.text,
              secondLinkText: secondLink.text,
              firstLink: (chunks: React.ReactNode) => (
                <PolicyLink
                  href={firstLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="privacy-policy-link"
                >
                  {chunks}
                </PolicyLink>
              ),
              secondLink: (chunks: React.ReactNode) => (
                <PolicyLink
                  href={secondLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="privacy-policy-link"
                >
                  {chunks}
                </PolicyLink>
              ),
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Please confirm that you have read and accept our <link>{linkText}</link>"
            description="Privacy policy confirmation message"
            values={{
              linkText: firstLink.text,
              link: (chunks: React.ReactNode) => (
                <PolicyLink
                  href={firstLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="privacy-policy-link"
                >
                  {chunks}
                </PolicyLink>
              ),
            }}
          />
        )}
      </Checkbox>
    );
  };

  return (
    <PrivacyPolicyContainer className="CheckoutWaiverContainer PrivacyPolicyContainer mobile-privacy-policy">
      {anyroadPrivacyPolicy ? (
        <Checkbox {...getFormProps('anyroad_privacy_policy')}>
          <FormattedMessage
            defaultMessage="I confirm I have read <termsLink>AnyRoad Terms</termsLink> and agree to the usage of your data according to <privacyLink>AnyRoad Privacy</privacyLink>"
            description="AnyRoad privacy policy confirmation text"
            values={{
              termsLink: (chunks: React.ReactNode) => (
                <PolicyLink
                  href={ANYROAD_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="privacy-policy-link"
                >
                  {chunks}
                </PolicyLink>
              ),
              privacyLink: (chunks: React.ReactNode) => (
                <PolicyLink
                  href={ANYROAD_PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="privacy-policy-link"
                >
                  {chunks}
                </PolicyLink>
              ),
            }}
          />
        </Checkbox>
      ) : (
        renderGuidePrivacyPolicy()
      )}
    </PrivacyPolicyContainer>
  );
};
